import { isEmpty, isEqual } from 'lodash-es';
import { CSSProperties, useCallback, useMemo } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { PosMultiSelect } from 'src/core/POS/PosMultiSelect';
import { MultiSelectValueChange } from 'src/core/POS/PosMultiSelect/PosMultiSelect.types';
import { ContentId } from 'src/utils/constants/contentId';
import { compareByNumericPrefix } from 'src/utils/tableUtils';
import { CompListingQuantity, Listing } from 'src/WebApiController';

import {
  quantitiesOptions,
  useDefaultQuantityFilters,
  useSelectedQuantityFilters,
} from './AutoPricingCompQuantitiesSelector.hook';

type AutoPricingCompQuantitiesSelectorV2Props = {
  listing?: Listing;
  disabled?: boolean;
  rootStyle?: CSSProperties;
  quantityFilters?: CompListingQuantity | null;
  quantityScoreAdjustmentOverrideJson?: string | null;
  onUpdateQuantityFilters: (
    quantityFilters: CompListingQuantity | null,
    quantityScoreAdjustmentOverrideJson: string | null,
    useAccountDefault?: boolean
  ) => void;
  leftIcon?: React.ReactNode;
  bulkProps?: {
    hasChanged: boolean;
  };
};

export const AutoPricingCompQuantitiesSelectorV2 = ({
  listing,
  disabled,
  rootStyle,
  quantityFilters,
  quantityScoreAdjustmentOverrideJson,
  leftIcon,
  bulkProps,
  onUpdateQuantityFilters,
}: AutoPricingCompQuantitiesSelectorV2Props) => {
  const defaultQuantityFilters = useDefaultQuantityFilters(listing);
  const defaultQuantitiesValues = useSelectedQuantityFilters(
    defaultQuantityFilters
  );
  const selectedQuantitiesValues = useSelectedQuantityFilters(
    quantityFilters,
    quantityScoreAdjustmentOverrideJson
  );

  const valuesDisplayText = useDisplayText(
    defaultQuantitiesValues,
    selectedQuantitiesValues,
    bulkProps,
    quantityScoreAdjustmentOverrideJson
  );

  const values = useMemo(() => {
    if (
      quantityFilters === null &&
      quantityScoreAdjustmentOverrideJson === null
    ) {
      return defaultQuantitiesValues;
    }
    return selectedQuantitiesValues;
  }, [
    quantityFilters,
    quantityScoreAdjustmentOverrideJson,
    selectedQuantitiesValues,
    defaultQuantitiesValues,
  ]);

  // Disable reset if already null (default with account settings)
  const disableReset = useMemo(() => {
    if (bulkProps) {
      return (
        bulkProps.hasChanged && quantityScoreAdjustmentOverrideJson == null
      );
    }
    return (
      quantityFilters == null && quantityScoreAdjustmentOverrideJson == null
    );
  }, [bulkProps, quantityFilters, quantityScoreAdjustmentOverrideJson]);

  const onQuantityFilterChange = useCallback(
    ({ newValues: values }: MultiSelectValueChange<string>) => {
      const exact: number[] = [];
      let gte: number | null = null;
      for (const value of values.toSorted(compareByNumericPrefix)) {
        const quantity = parseInt(value);
        if (value.indexOf('+') > -1) {
          gte = quantity;
        } else if (!isNaN(quantity)) {
          exact.push(quantity);
        }
      }

      const compListingQuantityFiltersNew = {
        exactQuantities: exact,
        greaterThanOrEqualToQuantity: gte,
      } as CompListingQuantity;
      const compListingQuantityScoreAdjustmentOverrideJsonNew = JSON.stringify({
        quantitiesFilter: { exact, gte },
      });

      onUpdateQuantityFilters(
        compListingQuantityFiltersNew,
        compListingQuantityScoreAdjustmentOverrideJsonNew,
        false
      );
    },
    [onUpdateQuantityFilters]
  );

  const onResetQuantities = useCallback(() => {
    onUpdateQuantityFilters(null, null, true);
  }, [onUpdateQuantityFilters]);

  return (
    <PosMultiSelect
      disabled={disabled}
      align="start"
      values={values}
      leftIcon={leftIcon}
      onChange={onQuantityFilterChange}
      sortFn={compareByNumericPrefix}
      valueOptionsContent={quantitiesOptions}
      valuesDisplayText={valuesDisplayText}
      triggerProps={{ style: rootStyle ?? { width: '100%', minWidth: '0' } }}
      contentFooterProps={{
        primaryButtonContentId: ContentId.ResetToDefaultSettings,
        primaryButtonVariant: 'textPlain',
        onPrimaryClick: onResetQuantities,
        disabled: disableReset,
      }}
    />
  );
};

/**
 * We want to indicate the user that we are using account default when
 * 1. The selected quantities are the same as the account default
 *    We have this because when the listing have the quantityFilter set to null,
 *    Pricer gRPC will default it to account quantity fitler, meaning ideally the quantities filter
 *    we see in the UI will never be null. But it can be that they are getting from account default
 * 2. The selected quantities is null
 * @param defaultQuantitiesValues
 * @param selectedQuantitiesValues
 * @param bulkProps
 * @param quantityScoreAdjustmentOverrideJson
 * @returns
 */
const useDisplayText = (
  defaultQuantitiesValues: string[],
  selectedQuantitiesValues: string[],
  bulkProps?: {
    hasChanged: boolean;
  },
  quantityScoreAdjustmentOverrideJson?: string | null
) => {
  const defaultText = useContent(ContentId.Default);
  const useAccountDefaultText = useContent(ContentId.UseAccountDefault);

  const quantitiesFilterNotSet = useMemo(() => {
    if (quantityScoreAdjustmentOverrideJson == null) {
      return true;
    }

    return (
      JSON.parse(quantityScoreAdjustmentOverrideJson)?.quantitiesFilter == null
    );
  }, [quantityScoreAdjustmentOverrideJson]);

  const isDefaultQuantities = useMemo(() => {
    return (
      !isEmpty(defaultQuantitiesValues) &&
      isEqual(
        defaultQuantitiesValues.toSorted(),
        selectedQuantitiesValues.toSorted()
      )
    );
  }, [defaultQuantitiesValues, selectedQuantitiesValues]);

  return useMemo(() => {
    if (bulkProps?.hasChanged && quantitiesFilterNotSet) {
      return `(${useAccountDefaultText})`;
    }

    // Default or null
    if (
      isDefaultQuantities ||
      (!isEmpty(defaultQuantitiesValues) && isEmpty(selectedQuantitiesValues))
    ) {
      const sortedValues = defaultQuantitiesValues
        .toSorted(compareByNumericPrefix)
        .join(', ');
      return `${sortedValues} (${defaultText})`;
    }
    const sortedValues = selectedQuantitiesValues
      .toSorted(compareByNumericPrefix)
      .join(', ');
    return sortedValues;
  }, [
    bulkProps?.hasChanged,
    defaultQuantitiesValues,
    defaultText,
    isDefaultQuantities,
    quantitiesFilterNotSet,
    selectedQuantitiesValues,
    useAccountDefaultText,
  ]);
};
